import { useState, useMemo, useEffect } from "react";
import { useBranch } from "@/context/BranchContext";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Eye,
  Pencil,
  QrCode,
  Plus,
  Download,
  Printer,
  MoreHorizontal,
  Loader2,
  Trash2,
  MoveRight,
  History,
  Users,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { useAssets, useDeleteAsset, type AssetListFilters, type AssetListRow } from "@/features/assets/api";
import { AssetFilters } from "@/features/assets/AssetFilters";
import { StatusBadge, AllocationStatusBadge } from "@/features/assets/badges";
import { QrModal, useQrModalState, printBulkLabels } from "@/features/assets/QrModal";
import { AllocateAssetDialog } from "@/features/assets/AllocateAssetDialog";
import { AssetKpis, type AssetKpiKey } from "@/features/assets/kpis";
import { shapeAssetRows } from "@/features/assets/excel";
import { ReportExportButton } from "@/features/reports/export/ReportExportButton";
import { useAssetListIndicators } from "@/features/assets/reliability";
import { AssetIndicatorBadges } from "@/features/assets/AssetIndicatorBadges";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canCreateAsset, canEditAsset, canDeleteAsset, canAllocateAsset, isAssetOnboardingComplete } from "@/lib/roles";
import type { AppRole } from "@/lib/roles";
import { assetActions } from "@/features/assets/allocationActions";
import { isDisposed } from "@/features/assets/lifecycle";
import { getDisplayAllocationStatus } from "@/features/assets/format";
import { cn } from "@/lib/utils";


export const Route = createFileRoute("/_authenticated/assets/")({
  component: AssetsPage,
});

function AssetsPage() {
  const navigate = useNavigate();

  const { data: me } = useCurrentUser();
  const roles = (me?.roles ?? []) as AppRole[];
  const canCreate = canCreateAsset(roles);
  const canEdit = canEditAsset(roles);
  const canDelete = canDeleteAsset(roles);
  const canExport = roles.includes("super_admin") || (me?.permissions?.includes("export_assets") ?? false);

  const { currentBranchId, canSwitch, lockedBranchId } = useBranch();

  const [filters, setFilters] = useState<AssetListFilters>({
    branchId: lockedBranchId ?? currentBranchId ?? null,
    includeInStore: false,
    page: 1,
    pageSize: 25,
  });

  // Keep the page-level branch filter in sync with the top-bar branch
  // context. Locked users are pinned to lockedBranchId; switchers follow
  // their selected branch.
  useEffect(() => {
    const next = lockedBranchId ?? currentBranchId ?? null;
    setFilters((f) => (f.branchId === next ? f : { ...f, branchId: next, page: 1 }));
  }, [currentBranchId, lockedBranchId]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [toDelete, setToDelete] = useState<AssetListRow | null>(null);
  const [toAllocate, setToAllocate] = useState<AssetListRow | null>(null);
  const [kpiFilter, setKpiFilter] = useState<AssetKpiKey | null>(null);
  const effectiveFilters = useMemo<AssetListFilters>(() => {
    if (!kpiFilter || kpiFilter === "total") return filters;
    const f: AssetListFilters = { ...filters, page: 1 };
    if (kpiFilter === "allocated") f.allocationStatus = "Allocated";
    else if (kpiFilter === "unallocated") f.allocationStatus = "Unallocated";
    else if (kpiFilter === "under_repair") f.status = "Under Repair";
    return f;
  }, [filters, kpiFilter]);
  const { data, isLoading } = useAssets(effectiveFilters);
  const deleteMut = useDeleteAsset();
  const queryClient = useQueryClient();
  const qrModal = useQrModalState();
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (filters.pageSize ?? 25)));
  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const { data: indicators } = useAssetListIndicators(rowIds);

  const selectableRows = useMemo(() => rows.filter((r) => !isDisposed(r)), [rows]);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(selectableRows.map((r) => r.id)));
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);

  const renderLocation = (a: AssetListRow) => a.location?.name ?? "—";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Asset Registry</h1> <ReadOnlyPill module="assets" />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: "/assets/qr-labels",
                search: someSelected
                  ? { selected: Array.from(selected).join(",") }
                  : {},
              })
            }
          >
            <Printer className="mr-1 h-4 w-4" />
            {someSelected ? `Print ${selected.size} QR Labels` : "Print QR Labels"}
          </Button>
          {someSelected && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <QrCode className="mr-1 h-4 w-4" />
                  Quick print {selected.size}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => printBulkLabels(selectedRows, "50x25")}>
                  50×25 mm labels
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => printBulkLabels(selectedRows, "75x50")}>
                  75×50 mm labels
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canExport && (
            <ReportExportButton
              reportKey="R-01"
              title="Asset Register"
              filename="Asset_Register"
              rows={() => shapeAssetRows(rows)}
              requiredColumns={["Asset Code"]}
              variant="outline"
              size="default"
              disabled={!rows.length}
            />
          )}
          {canCreate && (
            <Button variant="outline" onClick={() => navigate({ to: "/assets/import" })}>
              <Upload className="mr-1 h-4 w-4" /> Import CSV
            </Button>
          )}

          {canCreate && (
            <Button onClick={() => navigate({ to: "/assets/new" })}>
              <Plus className="mr-1 h-4 w-4" /> Add New Asset
            </Button>
          )}

        </div>
      </div>

      <AssetKpis
        branchId={filters.branchId ?? null}
        activeKey={kpiFilter}
        onSelect={(k) => setKpiFilter(k)}
      />

      <Card className="p-4">
        <AssetFilters filters={filters} onChange={setFilters} />
        <div className="mt-4 flex justify-end border-t pt-3 text-xs text-muted-foreground">
          Showing {rows.length} of {total} assets
        </div>
      </Card>


      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
              </TableHead>
              <TableHead>Asset Code</TableHead>
              <TableHead>Asset Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Asset Type</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Asset Status</TableHead>
              <TableHead>Allocation Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} className="py-12 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="py-12 text-center text-muted-foreground">
                  No assets found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((a) => {
                const disposed = isDisposed(a);
                const building = a.asset_class === "Infrastructure" || a.asset_type === "Building";
                const allowedTypes = ["Equipment","Infrastructure","Vehicle","IT","Furniture","Other"];
                const assetType = a.asset_class && allowedTypes.includes(a.asset_class) ? a.asset_class : "Unclassified";
                const assetName = a.category?.name ?? a.name;
                return (
                  <TableRow
                    key={a.id}
                    className={cn("cursor-pointer", disposed && "opacity-60 bg-muted/40 text-muted-foreground")}
                    onClick={() => navigate({ to: "/assets/$assetId", params: { assetId: a.id } })}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={disposed ? false : selected.has(a.id)}
                        onCheckedChange={() => !disposed && toggleOne(a.id)}
                        disabled={disposed}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {a.asset_code}
                      {a.asset_class === "Vehicle" && a.vehicle_registration_number && (
                        <div className="text-[10px] text-muted-foreground">{a.vehicle_registration_number}</div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {assetName}
                      <AssetIndicatorBadges assetId={a.id} indicator={indicators?.get(a.id)} />
                    </TableCell>
                    <TableCell className="max-w-[240px] text-sm text-muted-foreground">
                      {(a.item_description || a.name) ? (
                        <span className="line-clamp-2" title={a.item_description || a.name}>{a.item_description || a.name}</span>
                      ) : "—"}
                    </TableCell>
                    <TableCell><Badge variant="secondary">{assetType}</Badge></TableCell>
                    <TableCell>{a.branch?.name ?? "—"}</TableCell>
                    <TableCell>{a.department?.name ?? "—"}</TableCell>
                    <TableCell>{renderLocation(a)}</TableCell>
                    <TableCell>
                      <StatusBadge value={a.status} />
                    </TableCell>
                    <TableCell>
                      {building
                        ? <AllocationStatusBadge value="Not Applicable" />
                        : <AllocationStatusBadge value={getDisplayAllocationStatus(a)} />}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {disposed ? (
                            <>
                              <DropdownMenuItem
                                onClick={() => navigate({ to: "/assets/$assetId", params: { assetId: a.id } })}
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({ to: "/assets/$assetId", params: { assetId: a.id }, hash: "disposal" })
                                }
                              >
                                <History className="mr-2 h-4 w-4" />
                                View Disposal Record
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({ to: "/assets/$assetId", params: { assetId: a.id }, hash: "history" })
                                }
                              >
                                <History className="mr-2 h-4 w-4" />
                                View History
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => qrModal.show(a)}>
                                <QrCode className="mr-2 h-4 w-4" />
                                QR Label
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <>
                              {(() => {
                                const actions = assetActions(a);
                                const canAllocate = canAllocateAsset(roles, a);
                                const onboardingDone = isAssetOnboardingComplete(a);
                                const allocatorBase = roles.some((r) =>
                                  ["super_admin", "biomed_head", "facility_manager", "purchase_stores"].includes(r),
                                );
                                const showAllocateDisabled =
                                  allocatorBase &&
                                  !onboardingDone &&
                                  (actions.includes("allocate") || actions.includes("allocate_remaining"));
                                return (
                                  <>
                                    {canAllocate && onboardingDone && actions.includes("allocate") && (
                                      <DropdownMenuItem onClick={() => setToAllocate(a)}>
                                        <MoveRight className="mr-2 h-4 w-4" />
                                        Allocate
                                      </DropdownMenuItem>
                                    )}
                                    {canAllocate && onboardingDone && actions.includes("allocate_remaining") && (
                                      <DropdownMenuItem onClick={() => setToAllocate(a)}>
                                        <MoveRight className="mr-2 h-4 w-4" />
                                        Allocate Remaining
                                      </DropdownMenuItem>
                                    )}
                                    {showAllocateDisabled && (
                                      <DropdownMenuItem
                                        disabled
                                        title="Asset onboarding is not complete. Complete technical verification, finance capitalisation, and installation before allocation."
                                      >
                                        <MoveRight className="mr-2 h-4 w-4" />
                                        Allocate
                                      </DropdownMenuItem>
                                    )}
                                    {actions.includes("view_allocations") && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          navigate({
                                            to: "/assets/$assetId",
                                            params: { assetId: a.id },
                                            hash: "history",
                                          })
                                        }
                                      >
                                        <Users className="mr-2 h-4 w-4" />
                                        View Allocations
                                      </DropdownMenuItem>
                                    )}
                                    {canAllocate && actions.includes("request_transfer") && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          navigate({
                                            to: "/assets/$assetId/transfer/new",
                                            params: { assetId: a.id },
                                          })
                                        }
                                      >
                                        <MoveRight className="mr-2 h-4 w-4" />
                                        Request Transfer
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                );
                              })()}
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({ to: "/assets/$assetId", params: { assetId: a.id }, hash: "history" })
                                }
                              >
                                <History className="mr-2 h-4 w-4" />
                                Allocation History
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => navigate({ to: "/assets/$assetId", params: { assetId: a.id } })}
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                View
                              </DropdownMenuItem>
                              {canEdit && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate({ to: "/assets/$assetId/edit", params: { assetId: a.id } })
                                  }
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => qrModal.show(a)}>
                                <QrCode className="mr-2 h-4 w-4" />
                                QR Label
                              </DropdownMenuItem>
                              {canDelete && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => setToDelete(a)}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between border-t p-3 text-sm">
          <span className="text-muted-foreground">
            Page {filters.page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={(filters.page ?? 1) <= 1}
              onClick={() => setFilters({ ...filters, page: (filters.page ?? 1) - 1 })}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(filters.page ?? 1) >= totalPages}
              onClick={() => setFilters({ ...filters, page: (filters.page ?? 1) + 1 })}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>

      <QrModal asset={qrModal.asset} open={qrModal.open} onOpenChange={qrModal.onOpenChange} />

      <AllocateAssetDialog asset={toAllocate} open={!!toAllocate} onOpenChange={(o) => !o && setToAllocate(null)} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete asset?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <span className="font-mono">{toDelete?.asset_code}</span> — {toDelete?.name}.
              Linked documents, allocations, audit logs and history will also be removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!toDelete) return;
                try {
                  await deleteMut.mutateAsync(toDelete.id);
                  await queryClient.invalidateQueries({ queryKey: ["assets"] });
                  toast.success("Asset deleted");
                  setToDelete(null);
                } catch (e: unknown) {
                  toast.error((e as Error).message || "Delete failed");
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
